import { useState } from 'preact/hooks';
import { parseBullet } from '@/bullet';
import { choiceAddTime } from '@/options';
import { formatQty, formatTemp } from '@/qty';
import { clampServings, multiplierFor, scalingWarning } from '@/scaling';
import { formatTime } from '@/time';
import type { Recipe, UnitSystem } from '@/types';
import { CollapsibleSection } from './CollapsibleSection';
import { Icon } from './Icon';
import { SegmentedToggle } from './SegmentedToggle';

const filterInt = (s: string): string => s.replace(/\D/g, '');

/** `'sous-vide'` → `'Sous vide'`. */
function defaultOptionLabel(key: string): string {
  const spaced = key.replace(/-/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Render a choice label string, converting `{!145F}` temp tokens to the current unit system. */
function renderChoiceLabel(label: string, unitSystem: UnitSystem): string {
  return parseBullet(label)
    .map((seg) => {
      if (typeof seg === 'string') return seg;
      if ('temp' in seg) return formatTemp(seg.temp, seg.unit, unitSystem);
      return '';
    })
    .join('');
}

interface Props {
  recipe: Recipe;
  appliedServings: number;
  onApply: (n: number) => void;
  unitSystem: UnitSystem;
  onUnitSystemChange: (u: UnitSystem) => void;
  selection: Record<string, string>;
  onSelectionChange: (optionKey: string, choiceKey: string) => void;
}

export function Settings({
  recipe,
  appliedServings,
  onApply,
  unitSystem,
  onUnitSystemChange,
  selection,
  onSelectionChange,
}: Props) {
  const [draft, setDraft] = useState(String(appliedServings));
  const origServings = recipe.servings ?? 1;
  const draftNum = clampServings({ recipe, servings: Number(draft) });
  const liveMultiplier = multiplierFor({ recipe, servings: draftNum });
  const warning = scalingWarning({ recipe, servings: draftNum });
  const hasOptions = (recipe.options?.length ?? 0) > 0;
  const optionsCustom = (recipe.options ?? []).some((o) => selection[o.key] !== o.default);
  const subtitle = [
    `${unitSystem}, ${appliedServings} servings`,
    hasOptions ? (optionsCustom ? 'custom' : 'default') : null,
  ]
    .filter(Boolean)
    .join(', ');

  const commit = () => {
    onApply(draftNum);
    setDraft(String(draftNum));
  };

  return (
    <CollapsibleSection title="Settings" icon="cog" subtitle={subtitle} defaultOpen={false}>
      <div class="settings">
        <div class="settings__row">
          <span class="settings__label">Units</span>
          <SegmentedToggle
            options={['imperial', 'metric'] as const}
            value={unitSystem}
            onChange={onUnitSystemChange}
          />
        </div>

        <div class="settings__row">
          <span class="settings__label">Servings</span>
          <input
            class="settings__num"
            type="text"
            inputMode="numeric"
            enterkeyhint="done"
            value={draft}
            onInput={(e) => setDraft(filterInt(e.currentTarget.value))}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
            }}
          />
        </div>

        <div class="settings__row">
          <span class="settings__math">
            <span>{draftNum} servings</span>
            <span>=</span>
            <span>{formatQty(liveMultiplier)}</span>
            <span>×</span>
            <span>{origServings}</span>
            <span class="amount">(original recipe)</span>
          </span>
        </div>

        {warning ? (
          <div class="settings__warning">
            <Icon name="warning" size={18} />
            {warning}
          </div>
        ) : null}

        {(recipe.options ?? []).map((opt) => {
          const labels = Object.fromEntries(
            opt.choices.map((c) => {
              const base = c.label ?? c.key;
              const combined = c.subtitle ? `${base} (${c.subtitle})` : base;
              const resolved = combined.replace(/\{time\}/g, formatTime(choiceAddTime(c)));
              return [c.key, resolved];
            }),
          );
          return (
            <div class="settings__row" key={opt.key}>
              <span class="settings__label">{opt.label ?? defaultOptionLabel(opt.key)}</span>
              <SegmentedToggle
                options={opt.choices.map((c) => c.key)}
                value={selection[opt.key] ?? opt.default}
                onChange={(v) => onSelectionChange(opt.key, v)}
                renderLabel={(k) => renderChoiceLabel(labels[k] ?? k, unitSystem)}
                vertical
              />
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}
